/**
 * @bos/portal3 — WHAT THIS BUILD READ: the run's own record of the project's documents.
 *
 * The project page promises "they become the raw material this project is built from".
 * This screen is where that promise is kept or honestly broken, per document: what was
 * read into the brief (and how much of it), and what was held back (and why, in words).
 *
 * WORDING LAWS (the workbench's, applied here):
 *   - HELD IS NOT AN ERROR AND NOT AN APOLOGY. A scanned PDF or an old `.doc` is a fact
 *     about the file, said plainly, with the remedy when there is one. Nothing is red.
 *   - NOTHING IS INVENTED. Every number is a character count the server measured while
 *     reading the file. There is no percentage, no score, no "quality", no guess at what
 *     an unread document might have contained.
 *   - TRUNCATION IS SAID OUT LOUD, in the same place a person reads the document's name —
 *     a silent cut is the exact lie this product exists not to tell.
 *   - THE RECORD IS FROZEN AT LAUNCH and the screen says so: a document uploaded after
 *     this build started is not part of it, and the way to include it is a new build.
 *   - "NOT RECORDED" IS NOT "NONE". A run from before documents reached a build makes no
 *     claim about what was uploaded; it says only that it has no record.
 */
import type { RunDocumentsResult } from '@bos/portal3-server';
import type { StageReviewStatus } from './hooks';

const KIND_LABEL: Record<'doc' | 'pdf' | 'md', string> = {
  doc: 'Word',
  pdf: 'PDF',
  md: 'Markdown',
};

function formatCount(n: number): string {
  return n.toLocaleString();
}

/** The sentence under a truncated document — the cut, the size, and what is missing. */
export function truncationSentence(charsRead: number, charsAvailable: number, cap: number): string {
  return (
    `Cut at ${formatCount(cap)} characters. This document holds ${formatCount(charsAvailable)}; ` +
    `the first ${formatCount(charsRead)} are in this build and the rest are not.`
  );
}

interface RunDocumentsProps {
  readonly payload: RunDocumentsResult | null;
  readonly status: StageReviewStatus;
  readonly error: string | null;
}

export function RunDocuments({ payload, status, error }: RunDocumentsProps) {
  return (
    <section className="rd" aria-label="What this build read">
      <div className="rd__head">
        <h2 className="rd__title">What this build read</h2>
        <p className="rd__lede">
          Your uploaded documents are read as text and put in front of the machine together
          with your description. This is what it actually got.
        </p>
      </div>

      {status === 'loading' && (
        <p className="page__note" aria-busy="true">
          Reading this build’s record…
        </p>
      )}

      {status === 'error' && (
        <p className="form__error" role="alert">
          {error ?? 'We couldn’t load what this build read.'}
        </p>
      )}

      {status === 'ready' && payload !== null && <RunDocumentsBody payload={payload} />}
    </section>
  );
}

function RunDocumentsBody({ payload }: { payload: RunDocumentsResult }) {
  if (payload.state === 'unrecorded') {
    return (
      <p className="rd__empty">
        This build kept no record of documents — it started before Sorvel read documents into a
        build, or its record can’t be read now. That is not a claim that there were none.
      </p>
    );
  }

  if (payload.documents.length === 0) {
    return (
      <p className="rd__empty">
        No documents were attached to this project when this build started. It was built from
        your description alone.
      </p>
    );
  }

  const read = payload.documents.filter((d) => d.outcome === 'read');
  const held = payload.documents.filter((d) => d.outcome === 'held');

  return (
    <>
      <div className="rd__group">
        <h3 className="rd__group-title">
          Read into this build{read.length > 0 ? ` (${read.length})` : ''}
        </h3>
        {read.length === 0 ? (
          <p className="rd__empty">
            Nothing could be read from your documents, so this build was derived from your
            description alone. Every one of them is below, with the reason.
          </p>
        ) : (
          <ul className="rd__list">
            {read.map((doc) => (
              <li className="rd__row" key={doc.documentId}>
                <div className="rd__row-head">
                  <span className="rd__name" title={doc.filename}>
                    {doc.filename}
                  </span>
                  <span className="badge badge--kind">{KIND_LABEL[doc.kind]}</span>
                  <span className="rd__chars">
                    {formatCount(doc.charsRead ?? 0)} characters read
                  </span>
                </div>
                {doc.truncated === true && (
                  <p className="rd__cut">
                    {truncationSentence(
                      doc.charsRead ?? 0,
                      doc.charsAvailable ?? 0,
                      payload.perDocumentCap,
                    )}
                  </p>
                )}
                {doc.excerpt !== undefined && doc.excerpt !== '' && (
                  <p className="rd__excerpt">
                    <span className="rd__excerpt-label">It begins:</span> “{doc.excerpt.trim()}…”
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {held.length > 0 && (
        <div className="rd__group">
          <h3 className="rd__group-title">Held back ({held.length})</h3>
          <p className="rd__group-note">
            These are still on your project — nothing was deleted. They were not put in front of
            the machine, and it was not told about them.
          </p>
          <ul className="rd__list">
            {held.map((doc) => (
              <li className="rd__row rd__row--held" key={doc.documentId}>
                <div className="rd__row-head">
                  <span className="rd__name" title={doc.filename}>
                    {doc.filename}
                  </span>
                  <span className="badge badge--kind">{KIND_LABEL[doc.kind]}</span>
                </div>
                <p className="rd__reason">{doc.reasonText}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="rd__bounds">
        Sorvel reads up to {formatCount(payload.perDocumentCap)} characters from one document and{' '}
        {formatCount(payload.totalCap)} characters across all of them — past that a build’s brief
        is more than the machine can hold in one piece. This record was fixed when the build
        started: a document uploaded since then is not part of it, and starting a new build is
        what brings it in.
      </p>
    </>
  );
}
